import { Injectable, inject, signal, computed, OnDestroy, effect } from '@angular/core';
import { ElevatorService } from './elevator.service';
import { AlertService } from './alert.service';
import { AuthService } from './auth.service';
import { Elevator, Alert } from '../models';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ElevatorStateService implements OnDestroy {
  private elevatorService = inject(ElevatorService);
  private alertService = inject(AlertService);
  private authService = inject(AuthService);
  private eventSource?: EventSource;
  private reconnectTimeout?: ReturnType<typeof setTimeout>;

  // Raw states
  readonly elevators = signal<Elevator[]>([]);
  readonly activeAlerts = signal<Alert[]>([]);
  readonly totalBreakdownCount = signal<number>(0);

  // Computed states (Selectors)
  readonly totalCount = computed(() => this.elevators().length);
  readonly runningCount = computed(() => this.elevators().filter(e => e.status === 'RUNNING').length);
  readonly idleCount = computed(() => this.elevators().filter(e => e.status === 'IDLE').length);
  readonly errorCount = computed(() => this.elevators().filter(e => e.status === 'ERROR').length);
  readonly maintenanceCount = computed(() => this.elevators().filter(e => e.status === 'MAINTENANCE').length);

  // Status for header (RUNNING + IDLE = Available / Running)
  readonly headerRunningCount = computed(() => 
    this.elevators().filter(e => e.status === 'RUNNING' || e.status === 'IDLE').length
  );

  constructor() {
    effect(() => {
      if (this.authService.isLoggedIn()) {
        this.startSSE();
      } else {
        this.stopSSE();
        this.elevators.set([]);
        this.activeAlerts.set([]);
        this.totalBreakdownCount.set(0);
      }
    });
  }

  ngOnDestroy() {
    this.stopSSE();
  }

  private startSSE() {
    this.stopSSE();

    const token = localStorage.getItem('token');
    if (!token) return;

    const url = `${environment.apiUrl}/realtime/sse?token=${token}`;
    this.eventSource = new EventSource(url);

    this.eventSource.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.elevators) {
          this.elevators.set(payload.elevators);
        }
        if (payload.activeAlerts) {
          this.activeAlerts.set(payload.activeAlerts);
        }
        if (payload.totalBreakdownCount !== undefined) {
          this.totalBreakdownCount.set(payload.totalBreakdownCount);
        }
      } catch (err) {
        console.error('Failed to parse SSE payload:', err);
      }
    };

    this.eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      
      // If the EventSource is closed or not active, schedule a manual reconnect
      if (!this.eventSource || this.eventSource.readyState === EventSource.CLOSED) {
        this.scheduleReconnect();
      }
    };
  }

  private scheduleReconnect() {
    if (this.reconnectTimeout) return;
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = undefined;
      this.startSSE();
    }, 5000);
  }

  private stopSSE() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = undefined;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = undefined;
    }
  }

  public refresh() {
    // Re-establishing the connection forces an immediate data emission from the server
    this.startSSE();
  }
}
